import { useState, useEffect, useCallback, useRef } from 'react';
import { Search } from 'lucide-react';
import { useCollection } from '../hooks/useCollection';
import PokemonTCGService, { MIN_SEARCH_LENGTH } from '../services/PokemonTCGService';

const GlobalSearch = ({ onNavigate, compact = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const { navigateToCardSet } = useCollection();

  // Debounced search effect - THE CRITICAL PIECE
  useEffect(() => {
    // Measure the TRIMMED length — the server trims q before its own 2-char
    // guard, so " P" is effectively a 1-char query that can only ever come
    // back empty. Untrimmed measuring fired that doomed request and showed
    // "No cards found" instead of the type-more hint.
    const term = searchTerm.trim();
    if (term.length < MIN_SEARCH_LENGTH) {
      setResults([]);
      setShowResults(false);
      return;
    }

    // The timeout is cleared on re-run, but an already-started fetch isn't —
    // without the cancelled flag, a slow response for an old term can land
    // after a fast response for the current term and overwrite it.
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        setLoading(true);
        const searchResults = await PokemonTCGService.globalSearch(term);
        if (cancelled) return;
        setResults(searchResults);
        setShowResults(true);
      } catch (error) {
        if (cancelled) return;
        console.error('Global search error:', error);
        setResults([]);
        setShowResults(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  const handleCardClick = useCallback(async (card) => {
    try {
      const result = await navigateToCardSet(card);

      // Clear search state after successful navigation. Blur explicitly:
      // the dropdown's mousedown preventDefault keeps this input focused, and
      // the header search stays mounted across collection views — without the
      // blur, the mobile keyboard stays up over the card grid you just
      // navigated to.
      inputRef.current?.blur();
      setSearchTerm('');
      setResults([]);
      setShowResults(false);

      // Tell the parent (App) which view to switch to — replaces the old
      // implicit auto-nav effect that watched currentSet changing.
      if (onNavigate) {
        onNavigate(result.set);
      }
    } catch (error) {
      console.error('❌ Error navigating to card:', error);
      // TODO: Could show a toast error message here
    }
  }, [navigateToCardSet, onNavigate]);
  
  // Clear search - memoized to prevent unnecessary re-renders
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setResults([]);
    setShowResults(false);
  }, []);
  
  // Handle form submission (mostly just prevents page reload)
  const handleSubmit = (e) => {
    e.preventDefault();
    // The actual search is handled by the useEffect above
  };
  
  // BUG-008: this used to hide the dropdown 200ms after the input blurred.
  // On touch, dragging the results list to scroll blurs the input, so the list
  // vanished mid-scroll and read as "the results won't scroll". Close on a real
  // outside pointer press instead, so interacting with the list keeps it open.
  useEffect(() => {
    if (!showResults) return;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setShowResults(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setShowResults(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showResults]);

  const handleInputFocus = useCallback(() => {
    if (results.length > 0 && searchTerm.trim().length >= MIN_SEARCH_LENGTH) {
      setShowResults(true);
    }
  }, [results.length, searchTerm]);

  return (
    <div className="relative" ref={containerRef}>
      <form onSubmit={handleSubmit}>
        <div className="relative">
          {!compact && (
            <Search
              size={15}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none"
            />
          )}
          <input
            ref={inputRef}
            type="text"
            placeholder={compact ? 'Search cards...' : 'Search any card across all sets...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={handleInputFocus}
            autoComplete="off"
            spellCheck="false"
            className={
              compact
                ? 'w-full pl-4 pr-8 py-2 bg-white/10 border border-white/20 rounded-full text-white placeholder-white/50 text-[13px] focus:outline-none focus:ring-2 focus:ring-yellow'
                : 'w-full pl-9 pr-9 py-2.5 bg-white border border-border rounded-xl text-sm text-ink-primary placeholder-ink-faint focus:outline-none focus:ring-2 focus:ring-navy-dark/20'
            }
          />

          {/* Loading indicator */}
          {loading && (
            <div className={compact ? 'absolute right-8 top-2' : 'absolute right-9 top-1/2 -translate-y-1/2'}>
              <div className={compact ? 'animate-spin rounded-full h-4 w-4 border-2 border-white/60 border-t-transparent' : 'animate-spin rounded-full h-4 w-4 border-2 border-navy-dark/40 border-t-transparent'}></div>
            </div>
          )}

          {/* Clear Button */}
          {searchTerm.length > 0 && (
            <button
              type="button"
              onClick={clearSearch}
              className={compact ? 'absolute right-2.5 top-1.5 text-white/50 hover:text-white text-base transition-colors' : 'absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink-primary text-base transition-colors'}
            >
              ✕
            </button>
          )}
        </div>
      </form>

      {/* Search Results Dropdown */}
      {showResults && (
        <div
          // preventDefault on mousedown keeps focus in the input when the list
          // content is clicked — but ONLY for child targets. A mousedown whose
          // target is this element itself is the scrollbar (content hits always
          // land on children), and preventing that kills scrollbar dragging
          // entirely (confirmed: thumb drag left scrollTop at 0). A scrollbar
          // drag blurring the input is harmless now that closing is owned by
          // the outside-pointerdown handler, not blur.
          onMouseDown={(e) => {
            if (e.target !== e.currentTarget) e.preventDefault();
          }}
          className="absolute top-full left-0 right-0 bg-white border border-border rounded-xl shadow-card mt-1.5 max-h-96 overflow-y-auto overscroll-contain z-50"
        >
          {results.length === 0 ? (
            <div className="p-4 text-center text-ink-secondary">
              {loading ? (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-navy-dark/40 border-t-transparent"></div>
                  <span>Searching across sets...</span>
                </div>
              ) : (
                <div>
                  <div className="text-sm font-medium text-ink-primary">No cards found for "{searchTerm}"</div>
                  <div className="text-xs mt-1">Try a different card name or number</div>
                </div>
              )}
            </div>
          ) : (
            <div className="divide-y divide-border/60">
              <div className="px-3 py-2 bg-cream-bg/60 text-[11px] text-ink-secondary font-medium">
                Found {results.length} card{results.length === 1 ? '' : 's'} • Tap to view in set
              </div>
              {results.map((card) => (
                <div
                  key={`${card.id}-${card.setInfo.id}`}
                  className="p-3 hover:bg-cream-bg/60 cursor-pointer transition-colors"
                  onClick={() => handleCardClick(card)}
                >
                  <div className="flex items-center gap-3">
                    {/* Card Image */}
                    <img
                      src={card.images.small}
                      alt={card.name}
                      className="w-10 h-14 object-cover rounded border border-border shadow-sm flex-shrink-0"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA0OCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxjaXJjbGUgY3g9IjI0IiBjeT0iMzIiIHI9IjEwIiBmaWxsPSIjRDFENURCIi8+Cjwvc3ZnPg==';
                      }}
                    />

                    {/* Card Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-ink-primary truncate">
                          #{card.number} {card.name}
                        </span>
                        {card.rarity && (
                          <span className="text-[10px] bg-cream-bg text-ink-secondary px-1.5 py-0.5 rounded flex-shrink-0">
                            {card.rarity}
                          </span>
                        )}
                      </div>

                      <div className="text-xs text-ink-secondary truncate mt-0.5">
                        <span className="font-medium text-navy-light">{card.setInfo.name}</span>
                        <span className="mx-1.5 text-ink-faint">•</span>
                        <span>{card.setInfo.series}</span>
                        {card.setInfo.releaseDate && (
                          <>
                            <span className="mx-1.5 text-ink-faint">•</span>
                            <span>{new Date(card.setInfo.releaseDate).getFullYear()}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Navigation Arrow */}
                    <div className="text-ink-faint flex-shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search hint for short queries */}
      {!compact && searchTerm.length > 0 && searchTerm.trim().length < MIN_SEARCH_LENGTH && (
        <div className="mt-1.5 text-xs text-ink-faint text-center">
          Type at least {MIN_SEARCH_LENGTH} characters to search
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;